import React, { useState, useCallback, useEffect } from 'react';
import { Input } from 'antd';
import PropTypes from 'prop-types';

/**
 * Enhanced InputNumber component
 * - Handles leading decimals (e.g., ".25" -> "0.25")
 * - Enforces integer and decimal precision
 * - Prevents invalid keystrokes while allowing mid-typing states
 */
function InputNumber({
  value = '',
  onChange,
  decimalPlaces = 6,
  maxLength = 12,
  size = 'middle',
  style = { maxHeight: '36px' },
  placeholder = 'Add value',
  min,
  max,
  isPercentage = false,
  allowNegative = false,
  disabled = false,
  className,
  addonAfter,
  addonBefore,
  prefix,
  suffix,
  onKeyPress,
  onPaste,
  onBlur,
  onFocus,
  ...props
}) {
  const [internalValue, setInternalValue] = useState(value);

  // Sync internal state with prop value
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const sanitizeDecimal = useCallback(
    (raw, maxDecimals = decimalPlaces) => {
      if (decimalPlaces === 0) {
        return raw.toString().replace(/[^0-9]/g, '');
      }
      if (!raw && raw !== 0) return '';

      let cleanInput = raw.toString().replace(/[^0-9.]/g, '');

      // Allow standalone dot or leading dot during typing
      if (cleanInput === '.') return '0.';
      if (cleanInput.startsWith('.')) cleanInput = `0${cleanInput}`;

      const parts = cleanInput.split('.');
      let integerPart = parts[0] || '0';
      const decimalPart = parts.length > 1 ? parts[1] : '';

      // Remove leading zeros from integer part (but keep "0" alone)
      // Pattern: if integerPart matches /^0+[1-9]/ (starts with zeros followed by non-zero), remove leading zeros
      if (integerPart.length > 1 && integerPart.startsWith('0')) {
        // Remove all leading zeros, but if result is empty, keep "0"
        integerPart = integerPart.replace(/^0+/, '') || '0';
      }

      const limitedIntegerPart = integerPart.substring(0, maxLength);
      const limitedDecimalPart = decimalPart.substring(0, maxDecimals);

      if (parts.length > 1) {
        return `${limitedIntegerPart}.${limitedDecimalPart}`;
      }

      return limitedIntegerPart;
    },
    [decimalPlaces, maxLength],
  );

  const handleChange = useCallback(
    (e) => {
      const raw = e.target.value;

      // Allow user to clear the input
      if (raw === '') {
        setInternalValue('');
        if (onChange) onChange('');
        return;
      }

      // When decimalPlaces is 0, do not allow decimal point; otherwise allow typing "."
      if (raw === '.') {
        if (decimalPlaces === 0) {
          return;
        }
        setInternalValue('0.');
        if (onChange) onChange('0.');
        return;
      }

      let clean = sanitizeDecimal(raw);

      if (allowNegative && raw.startsWith('-')) {
        clean = `-${sanitizeDecimal(raw.substring(1))}`;
      }

      // Percentage constraint
      if (isPercentage) {
        const num = Number(clean);
        if (!Number.isNaN(num) && num > 100) {
          clean = clean.slice(0, -1);
        }
      }

      setInternalValue(clean);
      if (onChange) onChange(clean);
    },
    [sanitizeDecimal, decimalPlaces, allowNegative, isPercentage, onChange],
  );

  const handleKeyPress = useCallback(
    (e) => {
      const { key, keyCode, ctrlKey } = e;
      const { selectionStart } = e.target;
      const { selectionEnd } = e.target;
      const currentValue = e.target.value;

      // 1. Allow control keys
      if ([8, 9, 27, 13, 46].includes(keyCode) || (ctrlKey && [65, 67, 86, 88].includes(keyCode))) {
        return;
      }

      // 2. Only allow numbers, one dot, and one minus
      if (!/[0-9.]/.test(key) && !(allowNegative && key === '-')) {
        e.preventDefault();
        return;
      }

      // 3. When decimalPlaces is 0, do not allow decimal point; prevent multiple dots otherwise
      if (key === '.') {
        if (decimalPlaces === 0 || currentValue.includes('.')) {
          e.preventDefault();
          return;
        }
      }

      // 3.5. Prevent leading zeros: if integer part is "0" and user tries to type "0"
      if (key === '0') {
        const parts = currentValue.split('.');
        const intPart = parts[0].replace(/-/g, '');
        // If integer part is exactly "0" (single zero, no decimal point yet)
        // and user is trying to type "0", prevent it to avoid "00"
        if (intPart === '0' && !currentValue.includes('.')) {
          // Simulate what the new value would be
          const simulatedValue = currentValue.substring(0, selectionStart) + key + currentValue.substring(selectionEnd);
          // Allow replacement (if whole value selected and typing "0", that's fine)
          if (simulatedValue !== '0' && (simulatedValue.startsWith('00') || /^0+[1-9]/.test(simulatedValue))) {
            e.preventDefault();
            return;
          }
        }
      }

      // 4. Simulate result to validate limits
      const newValue = currentValue.substring(0, selectionStart) + key + currentValue.substring(selectionEnd);

      // Temporary conversion for validation
      let validationValue = newValue;
      if (validationValue === '.') validationValue = '0.';
      if (validationValue.startsWith('.')) validationValue = `0${validationValue}`;

      const parts = validationValue.split('.');
      const intPart = parts[0].replace(/-/g, '');
      const decPart = parts[1] || '';

      // Check Integer/Decimal length limits
      if (intPart.length > maxLength || decPart.length > decimalPlaces) {
        e.preventDefault();
        return;
      }

      // 5. Min/Max Logic
      const numValue = Number(validationValue);
      if (!Number.isNaN(numValue)) {
        const maxValue = isPercentage ? 100 : max;
        if (maxValue !== undefined && numValue > maxValue) {
          e.preventDefault();
          return;
        }

        // Only block MIN if it's not a "transitional" state (like 0 or 0.)
        if (min !== undefined && min > 0) {
          if (numValue < min && !['0', '0.', '0.0'].some((v) => validationValue.startsWith(v))) {
            // If typing a full number that is less than min, and not a decimal start
            if (!validationValue.includes('.')) {
              e.preventDefault();
              return;
            }
          }
        }
      }

      if (onKeyPress) onKeyPress(e);
    },
    [maxLength, decimalPlaces, max, min, isPercentage, allowNegative, onKeyPress],
  );

  const handleBlur = useCallback(
    (e) => {
      let val = e.target.value;
      if (!val) return;

      // Clean up trailing dots or leading dots on exit
      if (val.startsWith('.')) val = `0${val}`;
      if (val.endsWith('.')) val = val.slice(0, -1);

      // Final Min check on blur
      if (min !== undefined && Number(val) < min) {
        val = min.toString();
      }

      setInternalValue(val);
      if (onChange) onChange(val);
      if (onBlur) onBlur(e);
    },
    [min, onChange, onBlur],
  );

  return (
    <Input
      {...props}
      value={internalValue}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      onBlur={handleBlur}
      onPaste={onPaste}
      onFocus={onFocus}
      size={size}
      style={style}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      addonAfter={addonAfter}
      addonBefore={addonBefore}
      prefix={prefix}
      suffix={suffix}
      inputMode={decimalPlaces === 0 ? 'numeric' : 'decimal'}
    />
  );
}

InputNumber.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  decimalPlaces: PropTypes.number,
  maxLength: PropTypes.number,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  style: PropTypes.object,
  placeholder: PropTypes.string,
  errorMessage: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  isPercentage: PropTypes.bool,
  allowNegative: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  addonAfter: PropTypes.node,
  addonBefore: PropTypes.node,
  prefix: PropTypes.node,
  suffix: PropTypes.node,
  onKeyPress: PropTypes.func,
  onPaste: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
};

export default InputNumber;
